import React, { useEffect, useRef, useState } from "react";
import Button from "./Button";
import Pagination from "./Pagination";
import Loader from "./Loader";
import { useCarousel } from "./Carousel";

const resolveDirection = (prev, next, count) => {
  if (count <= 1) return "next";
  if (prev === count - 1 && next === 0) return "next";
  if (prev === 0 && next === count - 1) return "prev";
  if (prev === next) return "next";
  return next > prev ? "next" : "prev";
};

const layerEntranceClassMap = {
  layer0: "animate__fadeIn",
  layer1: "animate__slideInLeft",
  layer2: "animate__slideInUp",
};

const getLayerEntranceClass = (layerName) => layerEntranceClassMap[layerName] || "";

const getDecorEntranceClass = (item, layerName) => {
  if (layerName === "layer2" && (item?.type === "dp" || item?.type === "diagonal-panel")) {
    const itemClasses = `${item?.className || ""}`.toLowerCase();
    if (itemClasses.includes("left")) return "animate__slideInLeft";
    if (itemClasses.includes("right")) return "animate__slideInRight";
  }

  return getLayerEntranceClass(layerName);
};

export default function HeroBanner({
  images = [],
  title = "Hero Title",
  subtitle = "Subtitle",
  body = "Body copy for the hero banner.",
  current = 0,
  onChange,
  className = "",
  // Auto-scroll: pass true for default 5s, or a number (ms)
  autoScroll = false,
  // Pause auto-scroll when hovering the hero area
  pauseOnHover = true,
  // Show/hide navigation UI (chevrons + dots)
  showNavigation = true,
  // Show a top loading bar that indicates time until image advances
  showLoadingBar,
  // Optional fallback CTA when a slide does not provide one
  cta,
  // Content alignment within the hero grid
  contentAlign = "center",
  isLoading = false,
  variant = "default",
  parallax = false,
  parallaxStrengths,
  layers,
  layer0 = [],
  layer1,
  layer2 = [],
  layer3 = [],
  // Optional click handler for the hero background/content area
  // When provided, the hero becomes a clickable region (outside navigation controls)
  onHeroClick,
}) {
  // Normalize images to objects with src/title/subtitle/body
  const slides = Array.isArray(images)
    ? images.map((it) =>
        typeof it === "string"
          ? { src: it }
          : {
              src: it?.src || it?.image || it?.url || "",
              title: it?.title,
              subtitle: it?.subtitle,
              body: it?.body,
              cta: it?.cta,
            },
      )
    : [];
  const count = slides.length;

  const [direction, setDirection] = useState("next");
  const prevIndexRef = useRef(0);
  const heroRef = useRef(null);
  const intervalMs = typeof autoScroll === "number" ? autoScroll : 15000;
  const carousel = useCarousel({
    count,
    current,
    onChange,
    autoPlay: Boolean(autoScroll),
    intervalMs,
  });
  const idx = carousel.index;

  // Auto-advance timer (setTimeout) kept in sync with loading bar
  const [barKey, setBarKey] = useState(0);
  const isClickable = typeof onHeroClick === "function";

  useEffect(() => {
    if (prevIndexRef.current === idx) return;
    const nextDir = resolveDirection(prevIndexRef.current, idx, count);
    prevIndexRef.current = idx;
    setDirection(nextDir);
  }, [idx, count]);

  useEffect(() => {
    setBarKey(carousel.tick);
  }, [carousel.tick]);

  const currentSlide = count ? slides[idx] : undefined;
  const visibleLayers = {
    layer0: true,
    layer1: true,
    layer2: true,
    layer3: true,
    ...(layers || {}),
  };
  const resolvedParallaxStrengths = {
    layer0: 0.08,
    layer1: 0.14,
    layer2: 0.22,
    layer3: 0.3,
    ...(parallaxStrengths || {}),
  };
  const variantClass =
    variant && variant !== "default" ? `rm-hero--${variant}` : "";
  const normalizeLayerItems = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    return [value];
  };
  const isDecorItem = (value) =>
    Boolean(value) &&
    typeof value === "object" &&
    ("type" in value || "src" in value || "text" in value);
  const legacyLayer1 =
    typeof layer1 === "string" ? { type: "text", text: layer1 } : layer1;
  const layer0Items = normalizeLayerItems(layer0);
  const layer1Items = Array.isArray(legacyLayer1)
    ? normalizeLayerItems(legacyLayer1)
    : isDecorItem(legacyLayer1)
      ? normalizeLayerItems(legacyLayer1)
      : [];
  const layer2Items = normalizeLayerItems(layer2);
  const layer3Items = normalizeLayerItems(layer3);
  const titleText = currentSlide?.title ?? title;
  const subtitleText = currentSlide?.subtitle ?? subtitle;
  const bodyText = currentSlide?.body ?? body;
  const slideCta = currentSlide?.cta || cta;
  const shouldAnimate = count > 1;
  const contentClassName = [
    "rm-hero-content",
    shouldAnimate ? "animate__animated" : "",
    shouldAnimate
      ? direction === "prev"
        ? "animate__slideInLeft"
        : "animate__slideInRight"
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  const hoverProps = pauseOnHover
    ? {
        onMouseEnter: carousel.pause,
        onMouseLeave: carousel.resume,
      }
    : undefined;

  // Map contentAlign to a root modifier class (align-...)
  const alignClass = `align-${String(contentAlign || "center")
    .toLowerCase()
    .replace(/\s+/g, "-")}`;

  useEffect(() => {
    const root = heroRef.current;
    if (!root) return undefined;

    root.style.setProperty("--rm-hero-layer0-y", "0px");
    root.style.setProperty("--rm-hero-layer1-y", "0px");
    root.style.setProperty("--rm-hero-layer2-y", "0px");
    root.style.setProperty("--rm-hero-layer3-y", "0px");

    if (!parallax || typeof window === "undefined") return undefined;
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    );
    if (reduceMotion?.matches) return undefined;

    let rafId = 0;
    const update = () => {
      rafId = 0;
      const rect = root.getBoundingClientRect();
      const maxTravel = rect.height * 0.18;
      const clamp = (value) =>
        Math.min(Math.max(value, -maxTravel), maxTravel).toFixed(2);
      const calcOffset = (speed) => clamp(-rect.top * speed);

      root.style.setProperty(
        "--rm-hero-layer0-y",
        `${calcOffset(resolvedParallaxStrengths.layer0)}px`,
      );
      root.style.setProperty(
        "--rm-hero-layer1-y",
        `${calcOffset(resolvedParallaxStrengths.layer1)}px`,
      );
      root.style.setProperty(
        "--rm-hero-layer2-y",
        `${calcOffset(resolvedParallaxStrengths.layer2)}px`,
      );
      root.style.setProperty(
        "--rm-hero-layer3-y",
        `${calcOffset(resolvedParallaxStrengths.layer3)}px`,
      );
    };
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [parallax, resolvedParallaxStrengths]);

  const renderDecorItem = (item, itemIndex, layerName) => {
    const layerItemClass = `rm-hero-${layerName}-item`;
    const layerTypeClass = item.type
      ? `rm-hero-${layerName}-item--${item.type}`
      : "";
    const entranceClass = getDecorEntranceClass(item, layerName);
    const entranceDelay = `${0.22 * (itemIndex + 1)}s`;
    const className = [
      "rm-hero-layer-item",
      layerItemClass,
      item.className || "",
      layerTypeClass,
      item.type || "",
      entranceClass ? "animate__animated" : "",
      entranceClass,
    ]
      .filter(Boolean)
      .join(" ");

    if (item.type === "dp" || item.type === "diagonal-panel") {
      const repeatCount = item.repeat || 1;
      const baseId = item.id || `rm-hero-dp-${itemIndex + 1}`;
      const copies = Array.isArray(item.copies)
        ? item.copies
        : Array.from({ length: Math.max(1, repeatCount) }, () => ({}));

      return copies.map((copy, copyIndex) => (
        <div
          key={`${layerName}-${itemIndex}-${copyIndex}`}
          id={copy.id || `${baseId}-${copyIndex + 1}`}
          data-index={copyIndex + 1}
          className={[
            "rm-dp",
            item.className || "",
            copy.className || "",
            entranceClass ? "animate__animated" : "",
            entranceClass,
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
          style={{
            animationDelay: `${0.22 * (itemIndex + copyIndex + 1)}s`,
            "--motion-duration": "0.95s",
            "--animate-duration": "0.95s",
            ...(item.style || {}),
            ...(copy.style || {}),
          }}
        />
      ));
    }

    if ((item.type === "image" || item.src) && item.src) {
      return (
        <img
          key={`${layerName}-${itemIndex}`}
          className={[className].filter(Boolean).join(" ")}
          src={item.src}
          alt={item.alt || ""}
          style={{
            animationDelay: entranceDelay,
            "--motion-duration": "0.95s",
            "--animate-duration": "0.95s",
            ...(item.style || {}),
          }}
        />
      );
    }

    if ((item.type === "text" || item.text) && item.text) {
      return (
        <div
          key={`${layerName}-${itemIndex}`}
          className={[
            className,
            layerName === "layer1" ? "rm-hero-layer1-text" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{
            animationDelay: entranceDelay,
            "--motion-duration": "0.95s",
            "--animate-duration": "0.95s",
            ...(item.style || {}),
          }}
          aria-hidden="true"
        >
          {item.text}
        </div>
      );
    }

    return null;
  };

  const handleRootClick = (event) => {
    if (!isClickable) return;
    if (event && event.target && typeof event.target.closest === "function") {
      const ignore = event.target.closest(
        ".rm-hero-chevron, .rm-hero-chevrons, .rm-hero-dots, .rm-hero-slide-list",
      );
      if (ignore) return;
    }
    onHeroClick(event, { index: idx, slide: currentSlide });
  };

  const handleRootKeyDown = (event) => {
    if (!isClickable) return;
    const key = event.key || event.code;
    if (key !== "Enter" && key !== " " && key !== "Spacebar") return;
    if (event && event.target && typeof event.target.closest === "function") {
      const ignore = event.target.closest(
        ".rm-hero-chevron, .rm-hero-chevrons, .rm-hero-dots, .rm-hero-slide-list",
      );
      if (ignore) return;
    }
    event.preventDefault();
    onHeroClick(event, { index: idx, slide: currentSlide });
  };

  const content = (
    <section
      ref={heroRef}
      className={[
        "rm-hero",
        alignClass,
        variantClass,
        parallax ? "rm-hero--parallax" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-roledescription="carousel"
      aria-label="Hero banner"
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? handleRootClick : undefined}
      onKeyDown={isClickable ? handleRootKeyDown : undefined}
      {...hoverProps}
    >
      {/* Background slides for cross-fade */}
      {count > 0 && (
        <div className="rm-hero-bg rm-hero-layer rm-hero-layer--0" aria-hidden>
          {slides.map((s, i) => (
            <div
              key={i}
              className={[
                "rm-hero-slide",
                i === idx ? "is-active animate__animated animate__fadeIn" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ backgroundImage: `url(${s.src})` }}
            />
          ))}
        </div>
      )}
      <div className="rm-hero-foreground">
        {visibleLayers.layer0 && layer0Items.length > 0 && (
          <div className="rm-hero-layer rm-hero-layer--0" aria-hidden="true">
            {layer0Items.map((item, itemIndex) =>
              renderDecorItem(item, itemIndex, "layer0"),
            )}
          </div>
        )}
        {visibleLayers.layer1 && layer1Items.length > 0 && (
          <div className="rm-hero-layer rm-hero-layer--1" aria-hidden="true">
            {layer1Items.map((item, itemIndex) =>
              renderDecorItem(item, itemIndex, "layer1"),
            )}
          </div>
        )}
        {visibleLayers.layer2 && layer2Items.length > 0 && (
          <div className="rm-hero-layer rm-hero-layer--2" aria-hidden="true">
            {layer2Items.map((item, itemIndex) =>
              renderDecorItem(item, itemIndex, "layer2"),
            )}
          </div>
        )}
        {visibleLayers.layer3 && layer3Items.length > 0 && (
          <div className="rm-hero-layer rm-hero-layer--3" aria-hidden="true">
            {layer3Items.map((item, itemIndex) =>
              renderDecorItem(item, itemIndex, "layer3"),
            )}
          </div>
        )}
        {(showLoadingBar ?? Boolean(autoScroll)) && count > 1 && (
          <div
            className="rm-hero-loading"
            aria-hidden="true"
            key={`bar-${barKey}`}
            style={{
              "--loading-duration": `${intervalMs}ms`,
              animationPlayState: carousel.paused ? "paused" : "running",
            }}
          />
        )}
        <div className={contentClassName} key={`hero-content-${idx}-${direction}`}>
          {titleText && (
            <h1
              className="t-h1 animate__animated animate__fadeInUp"
              style={{
                animationDelay: "0.32s",
                "--motion-duration": "0.85s",
                "--animate-duration": "0.85s",
              }}
            >
              {titleText}
            </h1>
          )}
          {subtitleText && (
            <h2
              className="t-subhead animate__animated animate__fadeInUp"
              style={{
                animationDelay: "0.46s",
                "--motion-duration": "0.85s",
                "--animate-duration": "0.85s",
              }}
            >
              {subtitleText}
            </h2>
          )}
          {bodyText && (
            <p
              className="rm-hero-body t-body animate__animated animate__fadeInUp"
              style={{
                animationDelay: "0.6s",
                "--motion-duration": "0.85s",
                "--animate-duration": "0.85s",
              }}
            >
              {bodyText}
            </p>
          )}
          {slideCta && (
            <Button
              as="a"
              href={slideCta.href || slideCta.url || "#"}
              label={slideCta.label || "Learn more"}
              icon={slideCta.icon}
              primary
              size="sm"
              className="animate__animated animate__fadeInUp"
              style={{
                animationDelay: "0.74s",
                "--motion-duration": "0.85s",
                "--animate-duration": "0.85s",
              }}
            />
          )}
        </div>
        {count > 1 && showNavigation && (
          <>
            <div className="rm-hero-chevrons">
              <Button
                className="rm-hero-chevron rm-hero-chevron-prev"
                icon="chevron_left"
                iconOnly
                aria-label="Previous slide"
                onClick={(event) => {
                  if (event && typeof event.stopPropagation === "function") {
                    event.stopPropagation();
                  }
                  carousel.prev();
                }}
                as="a"
              />
              <Button
                className="rm-hero-chevron rm-hero-chevron-next"
                icon="chevron_right"
                iconOnly
                aria-label="Next slide"
                onClick={(event) => {
                  if (event && typeof event.stopPropagation === "function") {
                    event.stopPropagation();
                  }
                  carousel.next();
                }}
                as="a"
              />
            </div>
            <div className="rm-hero-dots">
              <Pagination
                total={slides.length}
                current={idx + 1}
                onChange={(p) => carousel.go(p - 1)}
                dotsOnly
              />
            </div>
          </>
        )}
      </div>
    </section>
  );

  if (isLoading) {
    return (
      <section
        className={[
          "rm-hero",
          alignClass,
          variantClass,
          parallax ? "rm-hero--parallax" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        aria-roledescription="carousel"
        aria-label="Hero banner loading"
      >
        <Loader />
      </section>
    );
  }

  return content;
}
